import { cx } from 'emotion';
import {
  CSSProperties,
  ComponentType,
  Fragment,
  ReactNode,
  Suspense,
  lazy,
  useCallback,
  Children,
  isValidElement,
  memo,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useDispatch, useSelector } from 'react-redux';

import { Spinner } from '@ezugi/components';
import { ErrorFallback, Icon, Scrollable } from '@ezugi/primitives';
import back from '@ezugi/static/icons/arrow-right.svg';
import close from '@ezugi/static/icons/close.svg';
import { GameWidgetKey } from '@ezugi/types';
import Drawer from '@ezugi/ui/components/Drawer/Drawer';
import { DeviceInfo } from '@ezugi/utils';

import GameVersion from '../../components/GameVersion';
import { useIntl } from '../../hooks/useIntl';
import { menuActions } from '../../store/actions';
import { SUPPORT, ROOM } from '../../store/constants/chat';
import { showGameInfoVersionSelector } from '../../store/selectors';
import { menuWidgetSelector } from '../../store/selectors/menu';
import { handleErrorBoundary } from '../../util/errorBoundary';
import { type GameLobbyProps } from '../../views/GameLobby';
import { miniLobbyPanelClassName } from '../../views/GameLobby/GameLobby.css';
import {
  bodyStyle,
  desktopBodyClassName,
  labelStyle,
  containerStyle,
  drawerContainer,
  desktopDrawerClassName,
  header,
  mobileDrawerContainer,
  labelTextStyle,
  chatHeaderClassName,
  chatDrawerClassName,
  tipsClassName,
  myBetsPanelMobileClassName,
  moreGamesHeaderClassName,
  scrollableClassName,
  iOSTableClassName,
  widgetLoadingContainer,
  chatFallbackBackgroundClass,
} from './GameWidgets.css';

const ICON_SIZE = 22;

// Default components
const PlayerHistoryWidget = lazy(() => import('./components/PlayerHistory'));
const GameLobby = lazy(() => import('../../views/GameLobby'));
const Tips = lazy(() => import('./components/Tips'));
const Chat = lazy(() => import('./components/Chat'));
const GameInfoBootstrap = lazy(() => import('./components/GameInfo'));
const SettingsBootstrap = lazy(() => import('./components/Settings'));

type GameInfoWrapperProps = {
  children: ReactNode;
};

const GameInfoWrapper = ({ children }: GameInfoWrapperProps) => {
  const showGameVersion = useSelector(showGameInfoVersionSelector);

  return (
    <Fragment>
      <div id="gameInfo">{children}</div>
      {showGameVersion ? <GameVersion /> : null}
    </Fragment>
  );
};

const WidgetLoading = memo(() => (
  <div className={widgetLoadingContainer}>
    <Spinner />
  </div>
));

const WidgetFallback = () => {
  const widgetKey = useSelector(menuWidgetSelector).key;
  const locale = useIntl({
    keys: {
      errorMessage: 'errors.something_went_wrong',
    },
  });

  const backgroundMobileChat = DeviceInfo.isHandheld && [ 'chat', 'liveSupport' ].includes(widgetKey ?? '');

  return (
    <ErrorFallback
      message={locale.errorMessage}
      className={cx({ [chatFallbackBackgroundClass]: backgroundMobileChat })}
    />
  );
};

type WidgetConfig = {
  mobileStyles?: {
    className?: string;
    panelStyle?: CSSProperties;
    panelClassName?: string;
    headerClassName?: string;
    leftIcon?: { svg: string };
  };
  desktopStyles?: {
    className?: string;
    panelClassName?: string;
    width?: string | number;
    height?: string | number;
  };
  headerClassName?: string;
  label?: string;
  icon?: string;
  svg?: string;
  width?: number;
  height?: number;
  drawerBackIcon?: boolean;
  renderInsideDrawer?: boolean;
  open?: boolean;
  chatType?: string;
};

type WidgetMapType = Record<
  GameWidgetKey,
  {
    component: ComponentType<any>;
  } & WidgetConfig
>;

type GameWidgetsProps = {
  children?: ReactNode[] | ReactNode;
  gameLobbyProps?: GameLobbyProps;
  customWidgetsConfig?: Partial<Record<GameWidgetKey, WidgetConfig>>;
};

const defaultWidgetConfigs: Record<GameWidgetKey, WidgetConfig> = {
  payouts: {
    mobileStyles: DeviceInfo.isIOS ? { className: iOSTableClassName } : {},
  },
  minilobby: {
    headerClassName: moreGamesHeaderClassName,
    desktopStyles: {
      width: '99%',
      height: '135%',
      panelClassName: miniLobbyPanelClassName,
    },
    mobileStyles: {
      panelClassName: miniLobbyPanelClassName,
    },
    renderInsideDrawer: false,
  },
  settings: {
    open: true,
  },
  tips: {
    headerClassName: tipsClassName,
    desktopStyles: { className: chatDrawerClassName },
    width: 400,
    height: 180,
  },
  stats: {},
  gameInfo: {},
  playerHistory: {},
  chat: {
    mobileStyles: {
      panelStyle: { background: 'transparent' },
      headerClassName: chatHeaderClassName,
    },
    desktopStyles: { className: chatDrawerClassName },
    chatType: ROOM,
    width: 250,
  },
  liveSupport: {
    mobileStyles: {
      panelStyle: { background: 'transparent' },
      headerClassName: chatHeaderClassName,
    },
    desktopStyles: { className: chatDrawerClassName },
    chatType: SUPPORT,
    width: 250,
  },
  myBets: {
    mobileStyles: {
      panelClassName: myBetsPanelMobileClassName,
      leftIcon: { svg: '' },
    },
  },
};

// Create base components with widgetType
export const createWidget = (
  Component: ComponentType<any>,
  widgetType: GameWidgetKey
): ComponentType<any> & { widgetType: GameWidgetKey } => {
  const WidgetComponent = (props: any) => (widgetType === 'gameInfo' ? (
    <GameInfoWrapper>
      <Component {...props} />
    </GameInfoWrapper>
  ) : (
    <Component {...props} />
  ));
  WidgetComponent.widgetType = widgetType;
  return WidgetComponent;
};

const DefaultGameInfoWidget = createWidget(GameInfoBootstrap, 'gameInfo');

const defaultComponents: Record<GameWidgetKey, ComponentType<any>> = {
  playerHistory: PlayerHistoryWidget,
  minilobby: GameLobby,
  tips: Tips,
  chat: Chat,
  gameInfo: DefaultGameInfoWidget,
  settings: SettingsBootstrap,
  payouts: Fragment,
  stats: Fragment,
  liveSupport: Chat,
  myBets: Fragment,
};

const useWidgetMap = (children: ReactNode[]) => useMemo(
  () => Children.toArray(children).reduce<Partial<WidgetMapType>>(
    (acc, child) => {
      if (isValidElement(child)) {
        const widgetType = (
              child.type as ComponentType<any> & { widgetType?: GameWidgetKey }
        ).widgetType;
        if (widgetType) {
          acc[widgetType] = {
            component: child.type as ComponentType<any>,
            ...defaultWidgetConfigs[widgetType],
            ...child.props,
          };
        }
      }
      return acc;
    },
    {
      ...Object.entries(defaultComponents).reduce<Partial<WidgetMapType>>(
        (acc, [ key, component ]) => ({
          ...acc,
          [key]: {
            component,
            ...defaultWidgetConfigs[key as GameWidgetKey],
          },
        }),
        {}
      ),
    }
  ),
  [ children ]
);

type OldWidget = {
  width?: number;
  height?: number;
  key?: string;
};

const GameWidgets = ({ children, customWidgetsConfig = {} }: GameWidgetsProps) => {
  const dispatch = useDispatch();
  const { key, drawer, ...widget } = useSelector(menuWidgetSelector);
  const [ oldWidget, setOldWidget ] = useState<OldWidget>({
    width: 0,
    height: 0,
    key: '',
  });

  // Build widget map from children
  const widgetComponentsMap = useWidgetMap(
    Array.isArray(children) ? children : [ children ]
  );

  const customWidgetConfig = customWidgetsConfig[key as GameWidgetKey] ?? {};

  const widgetConfig = (
    key ? widgetComponentsMap[key as GameWidgetKey] ?? {} : {}
  ) as Partial<WidgetMapType[GameWidgetKey]>;

  const {
    component: Widget,
    mobileStyles = {},
    desktopStyles = {},
    headerClassName,
    label = widget.label,
    icon = widget.icon,
    svg = widget.svg,
    drawerBackIcon = widget.drawerBackIcon,
    width,
    height,
    ...otherProps
  } = { ...widgetConfig, ...customWidgetConfig };

  const open = !!(key && drawer);

  useEffect(() => {
    if (open) {
      setOldWidget({
        width,
        height,
        key,
      });
    }
  }, [ open, width, key ]);

  const onLeftIconClick = useCallback(
    () => dispatch(menuActions.menu.set({ widget: {}, open: true })),
    [ dispatch ]
  );

  const onRightIconClick = useCallback(() => {
    dispatch(
      menuActions.menu.set({ widget: {}, open: false })
    );
  }, [ dispatch ]);

  return (
    <div
      className={cx(drawerContainer, {
        [mobileDrawerContainer]: DeviceInfo.isHandheld,
      })}
    >
      <Drawer
        data-e2e="game-widgets"
        open={open}
        align="right"
        title={label}
        icon={icon}
        drawerBackIcon={drawerBackIcon}
        leftIcon={back}
        onLeftIconClick={onLeftIconClick}
        rightIcon={close}
        onRightIconClick={onRightIconClick}
        {...(!DeviceInfo.isHandheld
          ? {
            width: width ?? (open ? 450 : oldWidget.width ?? 450),
            height:
                height
                ?? (oldWidget.height ?? ([ 'chat', 'liveSupport', 'settings' ].includes((key || oldWidget.key) ?? '')
                  ? '50%'
                  : '80%')),
            showBackdrop: false,
            showHeader: false,
            panelStyle: { borderRadius: '10px' },
            ...desktopStyles,
            className: cx(
              { [desktopDrawerClassName]: open },
              desktopStyles.className
            ),
          }
          : {
            ...mobileStyles,
            className: mobileStyles.className,
          })}
      >
        <div className={containerStyle}>
          {!DeviceInfo.isHandheld && (
            <div
              data-e2e="game-widgets-drawer-desktop-header"
              className={cx(header, headerClassName)}
            >
              <div className={labelStyle}>
                {!!svg && <Icon svg={svg} size={ICON_SIZE} />}
                {!!icon && <Icon svg={icon} size={ICON_SIZE} />}
                {!!label && <span className={labelTextStyle}>{label}</span>}
              </div>
              <Icon
                svg={close}
                size={ICON_SIZE}
                data-e2e="game-widgets-right-icon"
                onClick={onRightIconClick}
              />
            </div>
          )}
          <div
            className={cx(bodyStyle, {
              [desktopBodyClassName]: !DeviceInfo.isHandheld,
            })}
            data-e2e="game-widgets-drawer-body"
            data-testid="game-widgets-drawer-body"
          >
            <Scrollable
              key={key}
              updateOnChildrenChange={key === 'gameInfo'}
              className={cx({
                [scrollableClassName]:
                  !DeviceInfo.isHandheld && !DeviceInfo.isMacOS,
              })}
            >
              <ErrorBoundary
                FallbackComponent={WidgetFallback}
                onError={handleErrorBoundary}
                resetKeys={[ key ]}
              >
                {Widget ? (
                  <Suspense fallback={<WidgetLoading />}>
                    <Widget id={key} isDrawerOpen={open} {...otherProps} />
                  </Suspense>
                ) : null}
              </ErrorBoundary>
            </Scrollable>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

GameWidgets.Payouts = createWidget(Fragment, 'payouts');
GameWidgets.GameInfo = DefaultGameInfoWidget;
GameWidgets.Settings = createWidget(SettingsBootstrap, 'settings');
GameWidgets.Statistics = createWidget(Fragment, 'stats');
GameWidgets.MyBets = createWidget(Fragment, 'myBets');
GameWidgets.PlayerHistory = createWidget(PlayerHistoryWidget, 'playerHistory');
GameWidgets.Chat = createWidget(Chat, 'chat');
GameWidgets.LiveSupport = createWidget(Chat, 'liveSupport');
GameWidgets.Tips = createWidget(Tips, 'tips');
GameWidgets.GameLobby = createWidget(GameLobby, 'minilobby');

export default GameWidgets;
